import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchTemplateStructure } from './hrStructureService';
import { DEFAULT_HR_SECTIONS } from '../utils/hrMaterials';
import { listAllJobTreadImports } from './templatesImportsService';
import { listClaudeSkills } from './claudeSkillsService';

/**
 * Individual template content the CRM sidebar search can find — the CRM twin
 * of portalSearchContent.js. Covers the libraries staff manage from the
 * Templates hub: Sales / Operations / Finance / HR materials, SOP templates,
 * KPI roles, job descriptions, automations, JobTread imports, Claude skills,
 * and the software directory.
 *
 * Each entry is the same shape as the static CRM_SEARCH_ENTRIES (title,
 * section, keywords, plus the same access flags — requiresOwnerExecutive /
 * teams / memberAllowed — mirroring each page's route guard).
 *
 * Every path deep-links to the item itself:
 *   materials       → ?material=<title>  (activates the tab + highlights the card)
 *   SOPs / JDs      → ?open=<docId>      (opens the document modal)
 *   KPIs            → ?open=<role title> (opens the role modal)
 *   automations     → /crm/automation-templates/<id>
 *   imports         → ?type=<group>      (activates the matching tab)
 *   software        → ?search=<name>     (pre-fills the software search box)
 *
 * Loading is lazy (the sidebar only calls this once the user engages the
 * search box) and memoized for the life of the page.
 */

const DEPARTMENT_LABELS = {
  design: 'Design',
  construction: 'Construction',
  office: 'Office',
};

const norm = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

const departmentLabel = (department) =>
  DEPARTMENT_LABELS[String(department || '').toLowerCase()] || 'General';

/** One failing source must never blank out the rest of the index. */
const safe = (label, promise) =>
  promise
    .then((rows) => {
      console.warn(`[CRM search] source loaded: ${label} (${rows.length})`);
      return rows;
    })
    .catch((err) => {
      console.error(`CRM search: failed to load ${label}`, err);
      return [];
    });

const materialEntries = async ({ structureId, defaults, basePath, pillar, extraKeywords, flags }) => {
  const sections = await fetchTemplateStructure(structureId, defaults);
  return sections.flatMap((section) =>
    (section.items || []).map((item) => ({
      path: `${basePath}?material=${encodeURIComponent(item.title)}`,
      title: item.name || item.title,
      section: `${pillar} · ${section.label}`,
      keywords: norm(
        `${item.title} ${item.name || ''} ${item.description || ''} ${section.label} ${pillar} ${extraKeywords}`
      ),
      ...flags,
    }))
  );
};

const sopEntries = async () => {
  const snap = await getDocs(collection(db, 'sopTemplates'));
  return snap.docs.map((d) => {
    const sop = d.data();
    return {
      path: `/crm/sop-templates?open=${encodeURIComponent(d.id)}`,
      title: sop.title || 'Untitled SOP',
      section: `SOP templates · ${departmentLabel(sop.department)}`,
      keywords: norm(
        `${sop.title || ''} ${sop.description || ''} ${departmentLabel(sop.department)} sop sops standard operating procedure process template`
      ),
      requiresOwnerExecutive: true,
    };
  });
};

const kpiEntries = async () => {
  const snap = await getDocs(collection(db, 'kpiTemplates'));
  // The KPI templates page loads the first template it finds — mirror that so
  // the deep link always matches a role that's actually on the page.
  const template = snap.docs[0]?.data();
  const byDepartment = template?.departmentKPIs || {};
  return Object.entries(byDepartment).flatMap(([department, roles]) =>
    (roles || [])
      .filter((role) => role?.title)
      .map((role) => ({
        path: `/crm/kpi-templates?open=${encodeURIComponent(role.title)}`,
        title: role.title,
        section: `KPI templates · ${departmentLabel(department)}`,
        keywords: norm(
          `${role.title} ${departmentLabel(department)} kpi kpis metrics scorecard targets ${(role.kpis || [])
            .map((kpi) => `${kpi.title || ''} ${kpi.label || ''}`)
            .join(' ')}`
        ),
        requiresOwnerExecutive: true,
      }))
  );
};

const jobDescriptionEntries = async () => {
  const snap = await getDocs(collection(db, 'jobDescriptions'));
  return snap.docs.map((d) => {
    const job = d.data();
    return {
      path: `/crm/job-description-templates?open=${encodeURIComponent(d.id)}`,
      title: job.title || 'Untitled role',
      section: `Job description templates · ${departmentLabel(job.department)}`,
      keywords: norm(
        `${job.title || ''} ${job.description || ''} ${departmentLabel(job.department)} job description role position hiring responsibilities template`
      ),
      requiresOwnerExecutive: true,
    };
  });
};

const automationEntries = async () => {
  const snap = await getDocs(collection(db, 'automations'));
  return snap.docs.map((d) => {
    const automation = d.data();
    return {
      path: `/crm/automation-templates/${d.id}`,
      title: automation.name || automation.title || 'Untitled automation',
      section: 'Templates · Automations',
      keywords: norm(
        `${automation.name || ''} ${automation.title || ''} ${automation.description || ''} ${automation.category || ''} ${(
          automation.softwares || []
        )
          .map((s) => s?.name || s)
          .join(' ')} automation workflow zapier integration template`
      ),
    };
  });
};

const jobTreadImportEntries = async () => {
  const rows = await listAllJobTreadImports();
  return rows.map((row) => ({
    path: `/crm/templates/imports?type=${encodeURIComponent(row.tabId || 'cost-groups')}&highlight=${encodeURIComponent(row.id)}`,
    title: row.title || row.fileName || 'Untitled import',
    section: 'Templates · JobTread imports',
    keywords: norm(
      `${row.title || ''} ${row.description || ''} ${row.fileName || ''} ${row.tabId || ''} jobtread import template cost groups schedules dashboards todos csv json`
    ),
    memberAllowed: true,
  }));
};

const claudeSkillEntries = async () => {
  const rows = await listClaudeSkills();
  return rows.map((row) => ({
    path: `/crm/claude-skills-template?highlight=${encodeURIComponent(row.id)}`,
    title: row.title || row.skillFile?.fileName || 'Untitled skill',
    section: 'Templates · Skills',
    keywords: norm(
      `${row.title || ''} ${row.description || ''} ${row.skillFile?.fileName || ''} claude skill ai agent download template`
    ),
  }));
};

const softwareEntries = async () => {
  // Same walk AllSoftware.js does: technologyStack/{tier}/types/*/softwares.
  const tiers = ['Core', 'Primary', 'Secondary'];
  const entries = [];
  await Promise.all(
    tiers.map(async (tier) => {
      const typesSnap = await getDocs(collection(db, 'technologyStack', tier, 'types'));
      await Promise.all(
        typesSnap.docs.map(async (typeDoc) => {
          const typeName = typeDoc.data()?.name || '';
          const softwareSnap = await getDocs(
            collection(db, 'technologyStack', tier, 'types', typeDoc.id, 'softwares')
          );
          softwareSnap.forEach((softwareDoc) => {
            const data = softwareDoc.data();
            if (!data?.name) return;
            const useCases = Array.isArray(data.useCases)
              ? data.useCases.join(' ')
              : data.useCases || '';
            entries.push({
              path: `/crm/software?search=${encodeURIComponent(data.name)}`,
              title: data.name,
              section: `Software · ${typeName || tier}`,
              keywords: norm(
                `${data.name} ${data.description || ''} ${typeName} ${tier} ${useCases} software tool app tech stack`
              ),
            });
          });
        })
      );
    })
  );
  return entries;
};

/** Later duplicates lose — paths double as React keys in the results list. */
const dedupeByPath = (entries) => {
  const seen = new Set();
  return entries.filter((entry) => {
    if (!entry?.path || seen.has(entry.path)) return false;
    seen.add(entry.path);
    return true;
  });
};

let cachedPromise = null;

/**
 * Every individually searchable CRM template item. Memoized, so the
 * collections are read once per page load however often search is opened.
 */
export const loadCrmContentSearchEntries = () => {
  if (!cachedPromise) {
    console.warn('[CRM search] fetching content index');
    cachedPromise = Promise.all([
      safe(
        'sales materials',
        materialEntries({
          structureId: 'sales',
          defaults: [],
          basePath: '/crm/collection-sales',
          pillar: 'Sales materials',
          extraKeywords: 'sales material template document proposal contract resource collection',
          flags: { requiresOwnerExecutive: true },
        })
      ),
      safe(
        'operations materials',
        materialEntries({
          structureId: 'operations',
          defaults: [],
          basePath: '/crm/collection-operations',
          pillar: 'Operations materials',
          extraKeywords: 'operations material template document checklist resource collection',
          flags: { requiresOwnerExecutive: true },
        })
      ),
      safe(
        'finance materials',
        materialEntries({
          structureId: 'finance',
          defaults: [],
          basePath: '/crm/collection-finance',
          pillar: 'Finance materials',
          extraKeywords: 'finance material template document resource collection',
          flags: { teams: ['Finance'], memberAllowed: true },
        })
      ),
      safe(
        'HR materials',
        materialEntries({
          structureId: 'hr',
          defaults: DEFAULT_HR_SECTIONS,
          basePath: '/crm/collection-hr',
          pillar: 'HR materials',
          extraKeywords: 'hr material template document form resource collection',
          flags: { teams: ['HR'], memberAllowed: true },
        })
      ),
      safe('SOP templates', sopEntries()),
      safe('KPI templates', kpiEntries()),
      safe('job description templates', jobDescriptionEntries()),
      safe('automations', automationEntries()),
      safe('JobTread imports', jobTreadImportEntries()),
      safe('Skills', claudeSkillEntries()),
      safe('software tools', softwareEntries()),
    ])
      .then((groups) => {
        const entries = dedupeByPath(groups.flat());
        console.warn(`[CRM search] content index loaded: ${entries.length} entries`);
        // An all-empty index means every source failed — don't cache that for
        // the life of the page; let the next search open retry.
        if (!entries.length) cachedPromise = null;
        return entries;
      })
      .catch((err) => {
        // A total failure shouldn't poison the cache — let the next open retry.
        console.error('CRM search: content index failed', err);
        cachedPromise = null;
        return [];
      });
  }
  return cachedPromise;
};
